/**
 * @file PrivacyConsentModal.tsx
 * @description Component for rendering a modal to obtain user consent for privacy policies.
 * Manages the state of user consent and triggers a callback upon consent.
 * Utilizes UserDetailsContext to update user consent.
 */

import React, { useState, useEffect, RefObject } from "react";
import PrivacyConsentCheckbox from "./PrivacyConsentCheckbox";
import log from "../../config/logging";
import { useUserDetails } from "../../context/UserDetailsContext";

const PrivacyConsentModal: React.FC<{
  onConsent: () => void;
  dialogRef: RefObject<HTMLDialogElement>;
}> = ({ onConsent, dialogRef }) => {
  const [consentGiven, setConsentGiven] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const { updateUserConsent } = useUserDetails();

  useEffect(() => {
    log.debug("PrivacyConsentModal mounted, attempting to show modal");
    const currentDialog = dialogRef.current;
    const timer = setTimeout(() => {
      if (currentDialog) {
        currentDialog.showModal();
        log.debug("showModal called on dialogRef");
      } else {
        log.error("dialogRef is null in PrivacyConsentModal");
      }
    }, 0);
    return () => {
      clearTimeout(timer);
      log.debug("PrivacyConsentModal unmounted");
      if (currentDialog) {
        currentDialog.close();
        log.debug("close called on dialogRef");
      }
    };
  }, [dialogRef]);

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (consentGiven) {
      log.debug("User consent given");
      await updateUserConsent();
      onConsent();
    } else {
      setError("Devi fornire il consenso al trattamento dei dati personali.");
      log.error("User did not provide consent");
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="modal modal-bottom sm:modal-middle justify-normal"
    >
      <div className="modal-box bg-none">
        <h2 className="text-lg font-bold text-center normal-case text-cfh text-primary">
          Consenso privacy
        </h2>
        <form className="flex flex-col" onSubmit={handleConfirm}>
          <PrivacyConsentCheckbox
            checked={consentGiven}
            onChange={(checked) => {
              setConsentGiven(checked);
              if (checked) setError("");
            }}
          />
          <button
            type="submit"
            className="px-6 mb-4 text-lg font-bold uppercase border-none rounded-full text-formbutton text-primary-content bg-primary btn"
          >
            Conferma
          </button>
          {error && <p className="mt-4 text-center text-error">{error}</p>}
        </form>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button>chiudi</button>
      </form>
    </dialog>
  );
};

export default PrivacyConsentModal;
